import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Alert,
  TextField,
  CircularProgress,
  Paper
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import useThemeStyles from '../theme/useThemeStyles';

function NoWeddingAccess() {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const token = localStorage.getItem('token');
  const userType = localStorage.getItem('userType');

  useEffect(() => {
    if (userType === 'supplier' && user?.role === 'supplier') {
      navigate('/supplier/dashboard', { replace: true });
      return;
    }

    if (token && authLoading) {
      const timeout = setTimeout(() => setInitializing(false), 5000); // Increased timeout
      return () => clearTimeout(timeout);
    }

    // Don't remove token immediately if user isn't loaded - might be a fresh registration
    // Check if token was set recently (within last 10 seconds) - if so, wait longer
    const tokenSetTime = localStorage.getItem('tokenSetTime');
    const isFreshToken = tokenSetTime && (Date.now() - parseInt(tokenSetTime)) < 10000; // Within last 10 seconds
    
    if (token && !authLoading && !user && !isFreshToken) {
      // Only remove token if it's not a fresh registration
      localStorage.removeItem('token');
      localStorage.removeItem('userType');
      localStorage.removeItem('weddingId');
    }

    setInitializing(false);
  }, [token, authLoading, user, userType, navigate]);

  const submitJoinCode = useCallback(async (code) => {
    if (!code || code.length !== 6) return;

    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/wedding/request-access', {
        accessCode: code.trim(),
        role: 'friend',
        message: ''
      });

      setSuccess(`You’ve joined ${response.data.weddingName}. Taking you there now.`);
      setJoinCode('');

      if (response.data.weddingId) {
        localStorage.setItem('weddingId', response.data.weddingId);
        localStorage.removeItem('needsWedding');
        localStorage.removeItem('hasPendingInvites');
      }

      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 2000);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('token');
        localStorage.removeItem('userType');
        localStorage.removeItem('weddingId');
        setError('Your session has expired. Please log in again.');
        setTimeout(() => navigate('/login', { replace: true }), 2000);
        return;
      }
      setError(err.response?.data?.message || 'That code didn’t work. Please check it and try again.');
      setJoinCode('');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const digits = joinCode.replace(/\D/g, '');
    if (digits.length === 6 && !loading) {
      submitJoinCode(digits);
    }
  }, [joinCode, loading, submitJoinCode]);

  const handleJoinCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setJoinCode(value);
    setError('');
  };

  const handleStartPlanning = () => {
    navigate('/create-wedding');
  };

  if (initializing) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          backgroundImage: `url(${process.env.PUBLIC_URL}/img/brand-bg.jpg)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4
        }}
      >
        <CircularProgress size={40} sx={{ color: 'primary.main' }} />
        <Typography variant="body1" sx={{ mt: 2, color: 'text.primary' }}>
          Loading…
        </Typography>
      </Box>
    );
  }

  if (!token && !initializing) {
    navigate('/login', { replace: true });
    return null;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundImage: `url(${process.env.PUBLIC_URL}/img/brand-bg.jpg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 4,
        px: 2
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
        <img
          src={`${process.env.PUBLIC_URL}/img/logo.svg`}
          alt="Idotoo"
          style={{ height: 40 }}
        />
      </Box>

      <Container maxWidth="sm" disableGutters sx={{ textAlign: 'center' }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

        <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
          Choose how you’d like to get started.
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              textAlign: 'center'
            }}
          >
            <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 1, textAlign: 'center' }}>
              I’m planning my wedding
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Set up your wedding, add your date and details, and invite your partner when you’re ready.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleStartPlanning}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: '1rem',
                backgroundColor: 'primary.main',
                color: 'primary.contrastText',
                '&:hover': { backgroundColor: 'primary.dark' }
              }}
            >
              Start planning my wedding
            </Button>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              backgroundColor: 'background.paper',
              textAlign: 'center'
            }}
          >
            <Typography component="h2" className="pagename" sx={{ ...styles.text.pagename, mb: 1, textAlign: 'center' }}>
              I’m joining my partner’s wedding
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your partner can share a 6-digit code from their Idotoo wedding. Enter it below to join and plan together.
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter 6-digit code"
              value={joinCode}
              onChange={handleJoinCodeChange}
              disabled={loading}
              inputProps={{
                maxLength: 6,
                inputMode: 'numeric',
                pattern: '[0-9]*',
                'aria-label': '6-digit code from your partner'
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'background.paper'
                }
              }}
            />
          </Paper>
        </Box>
      </Container>
    </Box>
  );
}

export default NoWeddingAccess;
